import { cn } from "../lib/utils";
import React from "react";
import { BentoGrid, BentoGridItem } from "./ui/bento-grid";
import {
  IconAccessible,
  IconBoltFilled,
  IconDatabase,
  IconInfinity,
  IconLoader3,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { EvervaultCard } from "./ui/evervault-card";
import { OrbitingCirclesDemo } from "./OrbitingCircleSection";
import { CompareDemo } from "./CompareDemo";
import { AnimatedPinDemo } from "./AnimatedPinDemo";

export function BentoGridSection() {
  return (
    <>
      <div className="flex flex-col items-center pt-10">
        <div className="text-4xl text-[#16423C] md:text-5xl pb-4 font-bold text-center">
          Production of recycled compounds
        </div>
        <div className="text-xl text-[#16423C] font-medium mb-10 text-center max-w-5xl">
          Transform your recycled compound production by tapping into advanced
          insights on material properties and formulations, driven by automated
          processes and powerful analytical tools.
        </div>
      </div>
      <BentoGrid className="sm:max-w-6xl lg:max-w-6xl md:max-w-7xl md:mx-auto md:auto-rows-[20rem] sm:mb-12 mb-0 mt-12 mx-4">
        {items.map((item, i) => (
          <BentoGridItem
            key={i}
            title={item.title}
            description={item.description}
            header={item.header}
            className={cn("[&>p:text-lg]", item.className)}
            icon={item.icon}
          />
        ))}
      </BentoGrid>
    </>
  );
}

const SkeletonTwo = () => {
  const variants = {
    initial: {
      width: 0,
    },
    animate: {
      width: "100%",
      transition: {
        duration: 0.2,
      },
    },
    hover: {
      width: ["0%", "100%"],
      transition: {
        duration: 2,
      },
    },
  };

  const arr = new Array(6)
    .fill(0)
    .map(() => Math.random() * (100 - 40) + 40)
    .sort((a, b) => b - a);

  return (
    <motion.div
      initial="initial"
      animate="animate"
      whileHover="hover"
      className="flex flex-1 w-full sm:max-h-[6rem] bg-dot-black/[0.2] flex-col space-y-2"
    >
      {arr.map((width, i) => (
        <motion.div
          key={"skelenton-two" + i}
          variants={variants}
          style={{
            maxWidth: width + "%",
          }}
          className="flex flex-row  rounded-full border border-[#E9EFEC] p-2 items-center space-x-2 bg-white w-full h-4"
        ></motion.div>
      ))}
    </motion.div>
  );
};

export default SkeletonTwo;

const items = [
  {
    title: "Save Resources",
    description: (
      <span className="text-sm m-0 p-0">
        Fewer experiments are necessary to adjust formulations, saving time,
        material and energy.
      </span>
    ),
    header: <AnimatedPinDemo />,
    className: "md:col-span-1 border-2 border-grey-100 hover:border-none",
    icon: <IconDatabase className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: (
      <span className="mt-0">
        Stabilize Recyclate Properties and Predictive Processing
      </span>
    ),
    description: (
      <span className="text-sm  p-0">
        By combining data, expert knowledge, and AI, we predict the properties
        and processing of recycling compounds. Harness our AI pipelines to
        stabilize the inconsistent properties of recyclates, ensuring uniformity
        in compound production.
      </span>
    ),
    header: <CompareDemo />,
    className: "md:col-span-2 border-2 border-grey-100 hover:border-none mt-0",
    icon: <IconLoader3 className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: "Continuous Adjustment",
    description: (
      <span className="text-sm">
        Perform the necessary continuous adjustment of recycling compounds based
        on the properties of incoming recyclates to achieve consistent quality.
      </span>
    ),
    header: <EvervaultCard text="Tune" />,
    className: "md:col-span-2 border-2 border-grey-100 hover:border-none",
    icon: <IconInfinity className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: "Boost Efficiency",
    description: (
      <span className="text-sm">
        Enhance the efficiency in the production of recycling compounds while
        simultaneously saving costs (in the long term)
      </span>
    ),
    header: <SkeletonTwo />,
    className: "md:col-span-1 border-2 border-grey-100 hover:border-none",
    icon: <IconBoltFilled className="h-4 w-4 text-neutral-500" />,
  },
];
